// React
import * as React from 'react';
import _ from 'lodash';

// Internal components
import { ToolbarIconInfo, ToolbarIconInfo_GetSettingList } from '@Primitive/ToolbarIconButton';
import { TimeSeriesChartSettings, TimeSeriesChartLineSettings, ValidatePortState, ExcelTimezone, ExcelExportFormat } from './TimeSeriesStructs';
import * as WidgetsThingsStructs from '../WidgetsThingsStructs';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { eIconType, SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { getRandomSmallKey, isDefined, isFunction, IsNullOrEmpty } from '@Utils/Micro';
import { SettingsList } from '@Primitive/SettingsList';
import { WebThing, WebThingPort } from '@API/WebThing';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebNode } from '@API/WebNode';

const TimeSeriesChart = React.lazy(() => import("./TimeSeriesAsync"));
// =======================================================================================================
const enum TimeSeriesChartKeys {
    onToolbarIconClick = 'onToolbarIconClick',
};
// =======================================================================================================
class TimeSeriesChartWidgetSettings extends React.PureComponent<IWidgetSettingsProps<TimeSeriesChartSettings>> {
    // ------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<TimeSeriesChartSettings>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<TimeSeriesChartSettings>[] = [
            {
                name: "Toolbar",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: "Toolbar Custom Icons",
                        tooltip: "Extra custom icons in toolbar",
                        dataPath: "customToolbarIcons",
                        itemsInfo: ToolbarIconInfo_GetSettingList(),
                        createNewItem: () => {
                            return {
                                icon: "",
                                tooltip: "Icon tooltip",
                                iconType: eIconType.fontAwesome,
                                id: "Icon " + getRandomSmallKey(),
                                iconColor: "#2c2c2c",
                                nestedItems: []
                            } as ToolbarIconInfo;
                        }
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Height",
                        dataPath: "toolbarHeight",
                        minValue: 20,
                        maxValue: 80,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Icon Size",
                        dataPath: "toolbarIconSize",
                        minValue: 14,
                        maxValue: 50,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Default Icons Color',//todo labels[TSSavedDataChartUILabelKey.minColor],
                        tooltip: 'Set value for default Icons.',
                        dataPath: "defaultIconsColor",
                        // isHidden: (data: TimeSeriesSavedDataChartProps) => !data.showStats
                    },
                ]
            },
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: 'Datasets',
                        tooltip: 'Configures the collections of data.',
                        dataPath: 'Lines',
                        itemsInfo: [
                            {
                                type: SettingsType.String,
                                label: 'Label',
                                dataPath: 'Label'
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: 'Label color',
                                dataPath: 'BorderColor'
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: 'Line color',
                                dataPath: 'BackgroundColor'
                            },
                        ],
                        createNewItem: () => {
                            let thing = WebNode.GetInstanceKey(this.props.NodeKey).GetThing(this.props.ThingKey);
                            return TimeSeriesChartWidget.GetInitialDataset(thing);
                        }
                    },
                    {
                        type: SettingsType.Group,
                        name: "Chart Settings",
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Grid Lines',
                                tooltip: 'Shows grid lines for each time marker.',
                                dataPath: 'showGridLines'
                            },
                            {
                                type: SettingsType.Boolean,
                                label: 'Min/Max/Avg Lines',
                                tooltip: 'Shows additional lines for min/max/avg values.',
                                dataPath: 'showBaselines'
                            },
                            {
                                type: SettingsType.Boolean,
                                label: 'Show Points',
                                tooltip: 'Specifies whether to use a scatter chart.',
                                dataPath: 'showPoints'
                            },
                            {
                                type: SettingsType.Boolean,
                                label: 'Common Y-Axis',
                                tooltip: 'Specifies whether to use the same Y axis for all lines.',
                                dataPath: 'commonAxis'
                            },
                            {
                                type: SettingsType.Boolean,
                                label: 'Auto TimeRange',
                                tooltip: 'Automatically sets the timerange when new data is received.',
                                dataPath: 'autoTimeRange'
                            },
                            {
                                type: SettingsType.Boolean,
                                label: 'Focus Last Value',
                                tooltip: 'Αuto focus on the new value received.',
                                dataPath: 'focusLastValue'
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'Maximum number of elements to keep per dataset',
                                tooltip: 'Maximum number of historical data.',
                                dataPath: 'MaximumNumberOfElements',
                                items: [
                                    { label: "50", value: 50 },
                                    { label: "100", value: 100 },
                                    { label: "500", value: 500 },
                                    { label: "1000", value: 1000 },
                                    { label: "2000", value: 2000 },
                                    { label: "5000", value: 5000 }
                                ]
                            }
                        ]
                    },
                    {
                        name: "Excel Download",
                        type: SettingsType.Group,
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Excel Download',
                                tooltip: 'Allows to download an excel spreadsheet (XLSX file) with the extracted data from the chart',
                                dataPath: 'excelDownload'
                            },
                            {
                                type: SettingsType.String,
                                label: "File Name",
                                dataPath: "excelOptions.fileName",
                                tooltip: 'Define excel file name',
                                oneLine: true,
                                isHidden: (data: TimeSeriesChartSettings) => !data.excelDownload
                            },
                            {
                                type: SettingsType.String,
                                label: "Datetime Format",
                                dataPath: "excelOptions.datetimeFormat",
                                tooltip: 'Define datetime format',
                                oneLine: false,
                                isHidden: (data: TimeSeriesChartSettings) => !data.excelDownload
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Timezone",
                                tooltip: 'Choose desired timezone, between local and UTC',
                                dataPath: "excelOptions.timezone",
                                inSeperateLine: false,
                                items: [
                                    { value: ExcelTimezone.UTC, label: 'UTC' },
                                    { value: ExcelTimezone.Local, label: 'Local' }
                                ],
                                isHidden: (data: TimeSeriesChartSettings) => !data.excelDownload
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Export Format",
                                tooltip: 'Define exported file format, between xlsx, csv, or both',
                                dataPath: "excelExportFormat",
                                inSeperateLine: false,
                                items: [
                                    { value: ExcelExportFormat.Excel, label: 'Excel' },
                                    { value: ExcelExportFormat.CSV, label: 'CSV' },
                                    { value: ExcelExportFormat.Both, label: 'Both' }
                                ],
                                isHidden: (data: TimeSeriesChartSettings) => !data.excelDownload
                            },
                        ]
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isFunction(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
// TimeSeries chart Description interface

export class TimeSeriesChartWidget extends IWidgetThingsClass<TimeSeriesChartSettings> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget>
            <TimeSeriesChart />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <TimeSeriesChartWidgetSettings
            key={'Settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    private _getPorts(lines: TimeSeriesChartLineSettings[]): WebThingPort[] {
        return !isDefined(lines) ?
            [] :
            lines.map((line) =>
                new WebThingPort({
                    Name: line.Label,
                    PortKey: line.Id,
                    Type: Plegma.ePortType.Decimal,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'Adds a point to this line in chart',
                    State: ''
                }, WebThingPortType.Data,
                    (data) => ValidatePortState(line.Id, data)
                )
            ).concat([
                new WebThingPort({
                    Name: 'Reset',
                    PortKey: 'reset',
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.IsTrigger,
                    Description: 'Cleans any lines from chart',
                    State: ''
                }, WebThingPortType.Data,
                    (data) => ValidatePortState('reset', data)),
                new WebThingPort({
                    Name: 'Lines',
                    PortKey: 'lines',
                    Type: Plegma.ePortType.JsonString,
                    ioDirection: Plegma.ioPortDirection.Input,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: 'Display multiple lines in chart',
                    State: ''
                }, WebThingPortType.Data,
                    (data) => ValidatePortState('lines', data)),
                new WebThingPort({
                    Name: 'onToolbarIconClick',
                    PortKey: TimeSeriesChartKeys.onToolbarIconClick,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "",
                    State: ""
                }, WebThingPortType.Data)
            ]);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {

        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = this._getPorts(this.WidgetData.Lines)


        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add the ports
        this._getPorts(this.WidgetData.Lines).forEach((port) => {
            thing.AddPort(port, false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): TimeSeriesChartSettings {
        return {
            Lines: [],
            MaximumNumberOfElements: 100,
            showGridLines: false,
            showBaselines: false,
            showPoints: false,
            commonAxis: false,
            autoTimeRange: false,
            focusLastValue: false,
            excelDownload: false,
            excelOptions: {
                fileName: "Data",
                datetimeFormat: "MM-DD-YY hh:mm",
                timezone: ExcelTimezone.UTC
            },
            customToolbarIcons: [],
            toolbarHeight: 25,
            toolbarIconSize: 18,
            excelExportFormat: ExcelExportFormat.Both
        };
    }
    // ------------------------------------------------------------------------------------------------
    public static GetInitialDataset(thing: WebThing = null): TimeSeriesChartLineSettings {
        return {
            Id: isDefined(thing) ? thing.GetNextGeneratedPortKey() : 'data_' + getRandomSmallKey(),
            Label: 'My Dataset',
            BackgroundColor: 'rgba(255,0,0,1)',
            BorderColor: 'rgba(255,0,0,1)'
        }
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return TimeSeriesChartWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: TimeSeriesChartSettings = TimeSeriesChartWidget.GetDefaultWidgetDataStatic();

        // fill sample line
        data.Lines = [TimeSeriesChartWidget.GetInitialDataset()];

        return {
            Title: 'Time Series Chart',
            WidgetTitle: '',
            Key: 'TimeSeriesChartWidget',
            Type: 'TimeSeriesChartWidget',
            Data: data,
            Description: 'Time Series Chart Widget',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/timeline.svg'
        };
    }
    // ------------------------------------------------------------------------------------------------
    /**
     * Used to migrate data. Need to use `BackgroundColor`, `BorderColor` and `Label` to define `Lines`
     */
    public MigrateDefaultWidgetData(data: TimeSeriesChartSettings): TimeSeriesChartSettings {

        // check if data needs migration
        if (isDefined((data as any).BackgroundColor)) {
            data.Lines = [{
                BackgroundColor: (data as any).BackgroundColor,
                BorderColor: (data as any).BorderColor,
                Id: 'data',
                Label: (data as any).Label
            }];
            // remove old data
            delete (data as any).BackgroundColor;
            delete (data as any).BorderColor;
            delete (data as any).Label;
        }
        return data;
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        let res = {};

        // add widget's title (if any)
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        _.forEach(this.WidgetData.Lines, (v: TimeSeriesChartLineSettings) => {
            // Add Line's name to dictionary.
            res[v.Id] = v.Label;
        });

        return res;
    }
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================